import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { TENANT_ROLE_HIERARCHY, type TenantRole } from '@detect/shared';
import { useAuth, type TenantSummary } from './AuthContext';

/**
 * Tenant context (frontend-patterns §4): the active tenant for every
 * API call, persisted per-user, defaulting to the first membership.
 * Role helpers mirror the API's RBAC ladder — the API remains the
 * enforcement point; these only shape the UI.
 */

interface TenantContextType {
  tenantId: string | null;
  tenant: TenantSummary | null;
  tenants: TenantSummary[];
  setTenantId: (tenantId: string) => void;
  /** Superadmin-only: operate in a tenant WITHOUT a membership (the
   *  API's rbac ladder allows it; the platform panel is the entry). */
  enterTenant: (summary: TenantSummary) => void;
  /** UI-side ladder check (viewer < agent < supervisor < admin < account_admin). */
  hasRole: (required: TenantRole) => boolean;
  /** null = unrestricted; array = allowed client ids. */
  clientScope: string[] | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

function storageKey(userId: string): string {
  return `detect.activeTenant.${userId}`;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tenantIdState, setTenantIdState] = useState<string | null>(null);
  /** Session-only superadmin entry (never persisted — a stale entered
   *  tenant surviving a reload would be confusing, not convenient).
   *  Bound to the user who entered it: it is only honored while that
   *  same user is signed in AND still a superadmin, so a sign-out or an
   *  account switch can never hand it to someone else. */
  const [entered, setEntered] = useState<{
    userId: string;
    summary: TenantSummary;
  } | null>(null);
  const enteredTenant =
    entered && user && entered.userId === user.id && user.globalRole === 'superadmin'
      ? entered.summary
      : null;
  // Signing out ends a superadmin's tenant entry, even if the same account
  // signs straight back in.
  useEffect(() => {
    if (!user) setEntered(null);
  }, [user]);
  // Re-validated on every render against the CURRENT user's access, so a
  // tenant id left over from a previous user is never exposed, even for
  // the render before the resolve effect below catches up.
  const tenantId =
    tenantIdState !== null &&
    (user?.tenants.some((t) => t.tenantId === tenantIdState) ||
      enteredTenant?.tenantId === tenantIdState)
      ? tenantIdState
      : null;

  // Resolve the active tenant whenever the membership list changes:
  // persisted choice if still valid, else the first membership. A
  // superadmin-entered tenant wins while it is active.
  useEffect(() => {
    if (enteredTenant) return;
    if (!user || user.tenants.length === 0) {
      setTenantIdState(null);
      return;
    }
    const persisted = localStorage.getItem(storageKey(user.id));
    const valid = user.tenants.find((t) => t.tenantId === persisted);
    setTenantIdState(valid ? valid.tenantId : user.tenants[0].tenantId);
  }, [user, enteredTenant]);

  const setTenantId = useCallback(
    (next: string) => {
      if (!user) return;
      if (!user.tenants.some((t) => t.tenantId === next)) return; // fail-closed
      setEntered(null);
      localStorage.setItem(storageKey(user.id), next);
      setTenantIdState(next);
    },
    [user]
  );

  const enterTenant = useCallback(
    (summary: TenantSummary) => {
      if (!user || user.globalRole !== 'superadmin') return; // fail-closed
      setEntered({ userId: user.id, summary });
      setTenantIdState(summary.tenantId);
    },
    [user]
  );

  const tenant = useMemo(
    () =>
      user?.tenants.find((t) => t.tenantId === tenantId) ??
      (enteredTenant?.tenantId === tenantId ? enteredTenant : null),
    [user, tenantId, enteredTenant]
  );

  const hasRole = useCallback(
    (required: TenantRole): boolean => {
      if (!user) return false;
      if (user.globalRole === 'superadmin') return true;
      if (!tenant) return false;
      // A management grant's role is a ceiling: a global role never lifts it (the API does not either).
      const effective: TenantRole =
        user.globalRole === 'globaladmin' &&
        !tenant.delegated &&
        TENANT_ROLE_HIERARCHY[tenant.role] < TENANT_ROLE_HIERARCHY.admin
          ? 'admin'
          : tenant.role;
      return TENANT_ROLE_HIERARCHY[effective] >= TENANT_ROLE_HIERARCHY[required];
    },
    [user, tenant]
  );

  return (
    <TenantContext.Provider
      value={{
        tenantId,
        tenant,
        tenants: user?.tenants ?? [],
        setTenantId,
        enterTenant,
        hasRole,
        // null = unrestricted. An unrestricted MEMBERSHIP carries
        // `clientIds: null` ("all clients"); only the absence of a tenant is
        // the fail-closed empty scope — collapsing the two hid every client
        // from members the API treats as unrestricted.
        clientScope:
          user?.globalRole === 'superadmin' ? null : tenant ? tenant.clientIds : [],
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
